import { useMemo } from "react";
import { PowerSyncDatabase, Schema } from "@powersync/web";
import { useQuery, useStatus } from "@powersync/react";
import { fromRow, psTable } from "@cliqsuite/db/powersync";
import {
  businessSchema,
  categorySchema,
  kitchenTicketSchema,
  loyaltyConfigSchema,
  memberSchema,
  memberStorePointSchema,
  orderItemSchema,
  orderSchema,
  productSchema,
  productVariantSchema,
  storeInventorySchema,
  onlineSettingsSchema,
  productVariantGroupSchema,
  productVariantOptionSchema,
  promoSchema,
  rewardSchema,
  storeSchema,
  tableSchema,
  tableSessionSchema,
  voucherSchema,
} from "@cliqsuite/db/schemas";
import type { z } from "zod";

/** What a guest may know about a store — sync-api's GUEST_STORE_COLUMNS. */
export const guestStoreSchema = storeSchema.pick({
  id: true,
  business_id: true,
  name: true,
  address: true,
  city: true,
  store_icon: true,
  latitude: true,
  longitude: true,
  geofence_radius_m: true,
  guest_portal_enabled: true,
  currency_code: true,
  tax_rate: true,
  is_active: true,
});
/** The store row plus what it offers online; `online` comes from the guest-session reply, not from sync. */
export type GuestStore = z.infer<typeof guestStoreSchema> & { online?: z.input<typeof onlineSettingsSchema> | null; pay_accounts?: PayAccount[]; pay_methods?: string[] };
/** Where a guest can send a transfer: one of the store's own QR, bank or wallet accounts. */
export interface PayAccount {
  id: string;
  type: string;
  name: string;
  image_data: string | null;
  instructions: string | null;
  bank_name: string | null;
  account_name: string | null;
  account_number: string | null;
}

// Local table name → the zod row it holds. Names match the sync-rules output tables.
export const T = {
  stores: guestStoreSchema,
  businesses: businessSchema,
  categories: categorySchema,
  products: productSchema,
  product_variant_groups: productVariantGroupSchema,
  product_variant_options: productVariantOptionSchema,
  product_variants: productVariantSchema,
  store_inventory: storeInventorySchema,
  promos: promoSchema,
  rewards: rewardSchema,
  loyalty_configs: loyaltyConfigSchema,
  tables: tableSchema,
  table_sessions: tableSessionSchema,
  orders: orderSchema,
  order_items: orderItemSchema,
  kitchen_tickets: kitchenTicketSchema,
  members: memberSchema,
  member_store_points: memberStorePointSchema,
  vouchers: voucherSchema,
};

const schema = new Schema(Object.fromEntries(Object.entries(T).map(([name, s]) => [name, psTable(s)])));

// One tab per phone, and some in-app browsers (Instagram, LINE) lack SharedWorker.
export const db = new PowerSyncDatabase({ schema, database: { dbFilename: "guest.db", enableMultiTabs: false } });

/** Live rows of one local table, parsed back to their zod types. `sql` selects `*` from that table.
 * `isLoading` stays true until the first full sync, so "nothing here" never flashes before data lands. */
export function useRows<K extends keyof typeof T>(table: K, sql: string, params: unknown[] = []) {
  const { data, isLoading } = useQuery<Record<string, unknown>>(sql, params);
  const { hasSynced } = useStatus();
  const rows = useMemo(() => data.map((r) => fromRow(T[table], r)) as z.infer<(typeof T)[K]>[], [data, table]);
  return { data: rows, isLoading: isLoading || !hasSynced };
}
